import * as Dialog from "@radix-ui/react-dialog";
import { PlainDate, parsePlainDate } from "@sa-nu/shared-utils/date";
import { useState } from "react";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type { AccommodationNotice } from "../../../__generated__/gql/graphql";
import { WarnIcon } from "../dx-icons";
import { Link } from "../ui/link";

const Fragment = graphql(`
  fragment AccommodationNoticesForReservation on AccommodationReservation {
    accommodationNotices {
      brief
      detail
      externalUrl
    }
  }
`);

// TOOD: AccommodationNotice 全般、Flagment Colocation にするべき。これは暫定対応。
export type AccommodationNoticeForUser = Pick<
  AccommodationNotice,
  "brief" | "detail" | "externalUrl" | "startDate" | "endDate"
>;
type AccommodationNoticeForDisplay = Pick<
  AccommodationNotice,
  "brief" | "detail" | "externalUrl"
>;

export function AccommodationNotices({
  notices,
  firstDate,
  lastDate,
}: {
  notices: AccommodationNoticeForUser[];
  firstDate: PlainDate;
  lastDate: PlainDate;
}) {
  const filteredNotices = notices.filter((notice) => {
    const startDate = parsePlainDate(notice.startDate);
    const endDate = parsePlainDate(notice.endDate);
    return (
      PlainDate.compare(startDate, lastDate) < 0 &&
      PlainDate.compare(endDate, firstDate) >= 0
    );
  });
  return <CardNotices notices={filteredNotices} />;
}

export function AccommodationReservationNotices({
  reservation,
}: {
  reservation: FragmentType<typeof Fragment>;
}) {
  const notices = useFragment(Fragment, reservation).accommodationNotices;
  return <CardNotices notices={notices} />;
}

function CardNotices({
  notices,
}: {
  notices: AccommodationNoticeForDisplay[];
}) {
  const [notice, setNotice] = useState<AccommodationNoticeForDisplay | null>(
    null,
  );
  const [open, setOpen] = useState(false);

  if (notices.length === 0) return null;
  return (
    <div className="flex flex-col space-y-[15px]">
      {notices.map((notice, _i) => {
        const { brief, detail, externalUrl } = notice;
        return (
          <div key={brief}>
            <div className="border-sorange text-skogecha flex min-h-[60px] items-center justify-between space-x-[20px] border-b bg-white px-[20px] py-[10px] text-[9px] md:text-[10px]">
              <p className="leading-[140%] font-medium break-all">{brief}</p>
              {detail && (
                <div className="min-w-[23px]">
                  <DetailButton
                    data-analytics-id="accommodation_notices_opened_detail"
                    onClick={() => {
                      setOpen(true);
                      setNotice(notice);
                    }}
                  />
                </div>
              )}
              {!detail && externalUrl && (
                <Link
                  data-analytics-id="accommodation_notices_clicked_reference"
                  href={externalUrl}
                  target="_blank"
                >
                  <DetailButton onClick={() => {}} />
                </Link>
              )}
            </div>
          </div>
        );
      })}
      <CardNoticeDetailDialog
        open={open}
        onOpenChange={setOpen}
        notice={notice}
      />
    </div>
  );
}

export function ReservationNoticesSection({
  notices,
}: {
  notices: AccommodationNoticeForDisplay[];
}) {
  const [notice, setNotice] = useState<AccommodationNoticeForDisplay | null>(
    null,
  );
  const [open, setOpen] = useState(false);

  if (notices.length === 0) return null;

  return (
    <div className="flex flex-col gap-[12px]">
      <p className="font-notojp text-dx-text-default text-[16px] leading-[24px] font-bold">
        予約に関する注意事項
      </p>
      <div className="flex w-full flex-col gap-[12px]">
        {notices.map((n) => {
          const { brief, detail, externalUrl } = n;
          const cardClassName =
            "border-dx-stone-700 flex w-full items-start gap-[12px] rounded-[8px] border bg-white p-[12px] text-left";
          // 押下できるカード（detail→ダイアログ / externalUrl→リンク）は hover を揃える
          const interactiveCardClassName = `${cardClassName} transition-colors duration-150 hover:bg-dx-bg-base-hover`;
          const inner = (
            <>
              <WarnIcon className="text-dx-text-default size-[20px] shrink-0" />
              <p className="font-notojp text-dx-text-default text-[12px] leading-[1.6] font-bold break-words [word-break:break-word] underline">
                {brief}
              </p>
            </>
          );

          if (detail) {
            return (
              <button
                data-analytics-id="accommodation_notices_opened_detail"
                key={brief}
                type="button"
                className={interactiveCardClassName}
                onClick={() => {
                  setOpen(true);
                  setNotice(n);
                }}
              >
                {inner}
              </button>
            );
          }

          if (externalUrl) {
            return (
              <Link
                data-analytics-id="accommodation_notices_clicked_reference"
                key={brief}
                href={externalUrl}
                target="_blank"
                className={interactiveCardClassName}
              >
                {inner}
              </Link>
            );
          }

          return (
            <div key={brief} className={cardClassName}>
              {inner}
            </div>
          );
        })}
      </div>
      <CardNoticeDetailDialog
        open={open}
        onOpenChange={setOpen}
        notice={notice}
      />
    </div>
  );
}

function DetailButton({
  onClick,
  ...analytics
}: {
  onClick: () => void;
  /** 外部リンクで包む場合は外側の Link の id を拾わせるため省略する */
  "data-analytics-id"?: string;
}) {
  return (
    <button
      {...analytics}
      onClick={onClick}
      className="border-sorange mb-[6px] h-[20px] border-b font-bold tracking-widest"
    >
      詳細
    </button>
  );
}

function CardNoticeDetailDialog({
  open,
  onOpenChange,
  notice,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notice: AccommodationNoticeForDisplay | null;
}) {
  if (!notice) return null;

  const { brief, detail, externalUrl } = notice;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay bg-slightbeige items-start pt-20">
          <Dialog.Content
            aria-describedby={undefined}
            className="old-dialog-content md:w-[680px]"
          >
            <Dialog.Title className="hidden">施設のお知らせ</Dialog.Title>
            <div className="divide-skogecha border-sorange bg-swhite text-oldsblack space-y-[20px] divide-y divide-dotted rounded-none border-b p-[20px] md:p-[30px]">
              <p className="text-[13px] leading-[140%] font-medium break-all">
                {brief}
              </p>
              <div className="flex flex-col items-end space-y-[20px] pt-[20px]">
                <p className="w-full text-[11px] leading-[180%] font-normal break-all">
                  {detail}
                </p>
                {externalUrl && (
                  <Link
                    data-analytics-id="accommodation_notices_clicked_reference"
                    href={externalUrl}
                    target="_blank"
                  >
                    <button
                      // 一番近い印で打ち切るので、ignore にすると外側の Link の id まで届かない
                      data-analytics-id="accommodation_notices_clicked_reference"
                      className="border-sorange h-[20px] border-b pb-[6px] text-[9px] font-bold md:text-[10px]"
                    >
                      参照リンクはこちら
                    </button>
                  </Link>
                )}
              </div>
            </div>
            <button
              // 閉じるだけで、押されなかったことに情報が無い
              data-analytics-ignore
              onClick={() => {
                onOpenChange(false);
              }}
              className="bg-sbeige mt-[30px] h-[40px] w-full text-[10px] font-bold"
            >
              閉じる
            </button>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
